/**
 * @fileoverview Slider (range) input component for selecting numeric values
 * @module ctl-slider
 * @version 1.0.0
 * 
 * @description
 * A styled range slider component that allows users to select a numeric value by
 * dragging a thumb along a track. The component displays the current value and
 * supports min/max bounds, step increments, and optional tick marks.
 * 
 * The slider provides visual feedback with a filled track portion, supports
 * keyboard navigation (arrow keys), and can display labels for min/max values.
 * It's ideal for settings like volume, brightness, or any numeric range selection.
 * 
 * @example
 * <!-- Basic slider -->
 * <ctl-slider min="0" max="100" value="50">
 *   <span slot="label">Volume</span>
 * </ctl-slider>
 * 
 * @example
 * <!-- Slider with step and custom styling -->
 * <ctl-slider min="0" max="1000" step="50" value="500">
 *   <span slot="label">Price Range</span>
 * </ctl-slider>
 * 
 * @example
 * <!-- Disabled slider -->
 * <ctl-slider value="75" disabled>
 *   <span slot="label">Brightness</span>
 * </ctl-slider>
 */

const template = document.createElement("template");

template.innerHTML = `
<style>
  :host {
    --ctl-font: system-ui,-apple-system,Segoe UI,Roboto,Arial;

    display: block;
    font-family: var(--ctl-font);
    color: var(--ctl-color-text);
    width: 100%;
  }

  :host([disabled]) {
    opacity: var(--ctl-opacity-disabled);
    cursor: not-allowed;
  }

  .container {
    display: flex;
    flex-direction: column;
    gap: var(--ctl-space-3);
  }

  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: var(--ctl-font-size-base);
  }

  .label {
    font-weight: var(--ctl-font-weight-medium);
    color: var(--ctl-color-text);
  }

  .value {
    font-weight: var(--ctl-font-weight-semibold);
    color: var(--ctl-color-focus);
    font-variant-numeric: tabular-nums;
  }

  .slider-wrapper {
    position: relative;
    width: 100%;
    height: var(--ctl-slider-thumb-size);
    display: flex;
    align-items: center;
  }

  input[type="range"] {
    position: absolute;
    width: 100%;
    height: var(--ctl-slider-thumb-size);
    margin: 0;
    padding: 0;
    -webkit-appearance: none;
    appearance: none;
    background: transparent;
    cursor: pointer;
  }

  :host([disabled]) input[type="range"] {
    cursor: not-allowed;
  }

  /* Track */
  input[type="range"]::-webkit-slider-runnable-track {
    width: 100%;
    height: var(--ctl-slider-height);
    background: var(--ctl-color-border);
    border-radius: calc(var(--ctl-slider-height) / 2);
  }

  input[type="range"]::-moz-range-track {
    width: 100%;
    height: var(--ctl-slider-height);
    background: var(--ctl-color-border);
    border-radius: calc(var(--ctl-slider-height) / 2);
  }

  /* Thumb */
  input[type="range"]::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: var(--ctl-slider-thumb-size);
    height: var(--ctl-slider-thumb-size);
    border-radius: 50%;
    background: var(--ctl-color-focus);
    border: var(--ctl-slider-thumb-border-width) solid var(--ctl-color-white);
    box-shadow: var(--ctl-shadow-sm);
    cursor: pointer;
    margin-top: calc((var(--ctl-slider-track-height) - var(--ctl-slider-thumb-size)) / 2);
    transition: transform var(--ctl-transition-fast), box-shadow var(--ctl-transition-fast);
  }

  input[type="range"]::-moz-range-thumb {
    width: var(--ctl-slider-thumb-size);
    height: var(--ctl-slider-thumb-size);
    border-radius: 50%;
    background: var(--ctl-color-focus);
    border: var(--ctl-slider-thumb-border-width) solid var(--ctl-color-white);
    box-shadow: var(--ctl-shadow-sm);
    cursor: pointer;
    transition: transform var(--ctl-transition-fast), box-shadow var(--ctl-transition-fast);
  }

  input[type="range"]:hover::-webkit-slider-thumb {
    transform: scale(1.1);
    box-shadow: var(--ctl-shadow-md);
  }

  input[type="range"]:hover::-moz-range-thumb {
    transform: scale(1.1);
    box-shadow: var(--ctl-shadow-md);
  }

  input[type="range"]:active::-webkit-slider-thumb {
    transform: scale(1.15);
    box-shadow: var(--ctl-shadow-lg);
  }

  input[type="range"]:active::-moz-range-thumb {
    transform: scale(1.15);
    box-shadow: var(--ctl-shadow-lg);
  }

  input[type="range"]:focus-visible {
    outline: none;
  }

  input[type="range"]:focus-visible::-webkit-slider-thumb {
    outline: var(--ctl-focus-ring-width) solid var(--ctl-color-focus);
    outline-offset: var(--ctl-focus-ring-offset);
  }

  input[type="range"]:focus-visible::-moz-range-thumb {
    outline: var(--ctl-focus-ring-width) solid var(--ctl-color-focus);
    outline-offset: var(--ctl-focus-ring-offset);
  }

  /* Progress fill */
  .track {
    position: absolute;
    height: var(--ctl-slider-track-height);
    background: var(--ctl-color-border);
    border-radius: calc(var(--ctl-slider-track-height) / 2);
    width: 100%;
    pointer-events: none;
  }

  .progress {
    position: absolute;
    height: var(--ctl-slider-track-height);
    background: var(--ctl-color-focus);
    border-radius: calc(var(--ctl-slider-track-height) / 2);
    pointer-events: none;
    transition: width 0.05s ease;
  }

  .ticks {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: var(--ctl-space-2);
    font-size: var(--ctl-font-size-sm);
    color: var(--ctl-color-muted);
  }

  .tick {
    flex: 1;
    text-align: center;
  }

  .tick:first-child {
    text-align: left;
  }

  .tick:last-child {
    text-align: right;
  }

  :host([invalid]) .progress {
    background: var(--ctl-color-danger);
  }

  :host([invalid]) input[type="range"]::-webkit-slider-thumb {
    background: var(--ctl-color-danger);
  }

  :host([invalid]) input[type="range"]::-moz-range-thumb {
    background: var(--ctl-color-danger);
  }
</style>

<div class="container">
  <div class="header" part="header">
    <span class="label" part="label">
      <slot name="label"></slot>
    </span>
    <span class="value" part="value" id="valueDisplay"></span>
  </div>

  <div class="slider-wrapper" part="slider-wrapper">
    <div class="track" part="track"></div>
    <div class="progress" part="progress" id="progress"></div>
    <input
      type="range"
      id="input"
      part="input"
    />
  </div>

  <div class="ticks" part="ticks" id="ticks"></div>
</div>
`;

/**
 * Slider (Range) Web Component
 * 
 * A styled range slider for selecting numeric values within a defined range.
 * Features visual feedback with filled track and supports keyboard navigation.
 * 
 * @class CtlSlider
 * @extends HTMLElement
 * 
 * @attr {number} value - Current slider value
 * @attr {number} min - Minimum value (default: 0)
 * @attr {number} max - Maximum value (default: 100)
 * @attr {number} step - Step increment (default: 1)
 * @attr {boolean} disabled - Whether slider is disabled
 * @attr {boolean} required - Whether slider is required
 * @attr {boolean} invalid - Whether slider is in invalid state
 * @attr {string} name - Form field name for submission
 * @attr {boolean} show-ticks - Show tick marks along the slider
 * @attr {boolean} show-value - Show current value display
 * @attr {string} aria-label - Accessible label
 * @attr {string} aria-labelledby - ID of labeling element
 * @attr {string} aria-describedby - ID of describing element
 * @attr {boolean} aria-invalid - ARIA invalid state
 * 
 * @slot label - Optional label slot
 * 
 * @fires input - Dispatched when value changes during drag
 * @fires change - Dispatched when value changes and interaction completes
 * @fires ctl-input - Custom event dispatched on each input interaction with detail.value property
 * @fires ctl-change - Custom event with detail.value property
 * 
 * @cssprop --ctl-font - Font family (default: system-ui,-apple-system,Segoe UI,Roboto,Arial)
 * @cssprop --ctl-color-text - Text color (default: #111827)
 * @cssprop --ctl-color-muted - Muted text color (default: #6b7280)
 * @cssprop --ctl-color-border - Border/track color (default: #e5e7eb)
 * @cssprop --ctl-color-surface - Background color (default: #ffffff)
 * @cssprop --ctl-color-focus - Active/filled track color (default: #4f46e5)
 * @cssprop --ctl-color-danger - Error/invalid color (default: #dc2626)
 * @cssprop --ctl-slider-height - Height of slider track (default: 6px)
 * @cssprop --ctl-slider-thumb-size - Size of slider thumb (default: 20px)
 */
export class CtlSlider extends HTMLElement {
  /**
   * Observed HTML attributes that trigger attributeChangedCallback
   * @returns {string[]} Array of attribute names to observe
   */
  static get observedAttributes() {
    return [
      "value",
      "min",
      "max",
      "step",
      "disabled",
      "required",
      "invalid",
      "name",
      "show-ticks",
      "show-value",
      "aria-label",
      "aria-labelledby",
      "aria-describedby",
      "aria-invalid"
    ];
  }

  constructor() {
    super();
    this.attachShadow({ mode: "open" }).appendChild(template.content.cloneNode(true));
    this._input = this.shadowRoot.getElementById("input");
    this._progress = this.shadowRoot.getElementById("progress");
    this._valueDisplay = this.shadowRoot.getElementById("valueDisplay");
    this._ticks = this.shadowRoot.getElementById("ticks");

    this._onInput = this._onInput.bind(this);
    this._onChange = this._onChange.bind(this);
  }

  connectedCallback() {
    this._upgradeProperty("value");
    this._upgradeProperty("min");
    this._upgradeProperty("max");
    this._upgradeProperty("step");
    this._upgradeProperty("disabled");
    this._upgradeProperty("name");

    this._input.addEventListener("input", this._onInput);
    this._input.addEventListener("change", this._onChange);

    this._syncToInput();
    this._updateProgress();
    this._renderTicks();
  }

  disconnectedCallback() {
    this._input.removeEventListener("input", this._onInput);
    this._input.removeEventListener("change", this._onChange);
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    
    // Selective updates based on attribute
    switch (name) {
      case "value":
        this._syncToInput(name);
        this._updateProgress();
        break;
        
      case "min":
      case "max":
        this._syncToInput(name);
        this._updateProgress();
        // Only re-render ticks if show-ticks is enabled
        if (this.hasAttribute('show-ticks')) {
          this._renderTicks();
        }
        break;
        
      case "step":
        this._syncToInput(name);
        // Only re-render ticks if show-ticks is enabled
        if (this.hasAttribute('show-ticks')) {
          this._renderTicks();
        }
        break;
        
      case "show-ticks":
        // Toggle tick visibility
        if (newValue !== null) {
          this._renderTicks();
        } else {
          this._clearTicks();
        }
        break;
        
      case "disabled":
        this._syncToInput(name);
        break;
        
      default:
        this._syncToInput(name);
    }
  }

  // --- Properties ---
  get value() {
    return parseFloat(this.getAttribute("value")) || 0;
  }

  set value(val) {
    const num = parseFloat(val);
    if (!isNaN(num)) {
      this.setAttribute("value", String(num));
    }
  }

  get min() {
    return parseFloat(this.getAttribute("min")) || 0;
  }

  set min(val) {
    const num = parseFloat(val);
    if (!isNaN(num)) {
      this.setAttribute("min", String(num));
    }
  }

  get max() {
    return parseFloat(this.getAttribute("max")) || 100;
  }

  set max(val) {
    const num = parseFloat(val);
    if (!isNaN(num)) {
      this.setAttribute("max", String(num));
    }
  }

  get step() {
    return parseFloat(this.getAttribute("step")) || 1;
  }

  set step(val) {
    const num = parseFloat(val);
    if (!isNaN(num)) {
      this.setAttribute("step", String(num));
    }
  }

  get disabled() {
    return this.hasAttribute("disabled");
  }

  set disabled(value) {
    if (value) this.setAttribute("disabled", "");
    else this.removeAttribute("disabled");
  }

  get required() {
    return this.hasAttribute("required");
  }

  set required(value) {
    if (value) this.setAttribute("required", "");
    else this.removeAttribute("required");
  }

  get name() {
    return this.getAttribute("name");
  }

  set name(val) {
    if (val == null) this.removeAttribute("name");
    else this.setAttribute("name", String(val));
  }

  focus(options) {
    this._input.focus(options);
  }

  blur() {
    this._input.blur();
  }

  // --- Internals ---
  _upgradeProperty(prop) {
    if (Object.prototype.hasOwnProperty.call(this, prop)) {
      const value = this[prop];
      delete this[prop];
      this[prop] = value;
    }
  }

  _syncToInput(changedName) {
    if (!changedName || changedName === "value") {
      this._input.value = String(this.value);
      this._updateValueDisplay();
    }

    if (!changedName || changedName === "min") {
      this._input.min = String(this.min);
    }

    if (!changedName || changedName === "max") {
      this._input.max = String(this.max);
    }

    if (!changedName || changedName === "step") {
      this._input.step = String(this.step);
    }

    if (!changedName || changedName === "disabled") {
      this._input.disabled = this.hasAttribute("disabled");
    }

    if (!changedName || changedName === "required") {
      this._input.required = this.hasAttribute("required");
    }

    if (!changedName || changedName === "name") {
      const name = this.getAttribute("name");
      if (name) this._input.setAttribute("name", name);
      else this._input.removeAttribute("name");
    }

    if (!changedName || changedName === "show-value") {
      this._valueDisplay.style.display = this.hasAttribute("show-value") ? "block" : "none";
    }

    // ARIA attributes
    const ariaAttrs = ["aria-label", "aria-labelledby", "aria-describedby", "aria-invalid"];
    for (const attr of ariaAttrs) {
      if (!changedName || changedName === attr) {
        if (this.hasAttribute(attr)) {
          this._input.setAttribute(attr, this.getAttribute(attr));
        } else {
          this._input.removeAttribute(attr);
        }
      }
    }
    
    // Add ARIA attributes for range slider accessibility
    // Only update when initializing or when value-related properties change
    if (!changedName || changedName === "min" || changedName === "max" || changedName === "value") {
      this._input.setAttribute("aria-valuemin", String(this.min));
      this._input.setAttribute("aria-valuemax", String(this.max));
      this._input.setAttribute("aria-valuenow", String(this.value));
    }

    // Sync aria-invalid based on invalid attribute if not explicitly set
    if (!changedName || changedName === "invalid") {
      if (!this.hasAttribute("aria-invalid")) {
        if (this.hasAttribute("invalid")) {
          this._input.setAttribute("aria-invalid", "true");
        } else {
          this._input.removeAttribute("aria-invalid");
        }
      }
    }
  }

  _updateProgress() {
    const min = this.min;
    const max = this.max;
    const value = this.value;
    const range = max - min;
    const percent = range === 0 ? 0 : ((value - min) / range) * 100;
    this._progress.style.width = `${percent}%`;
  }

  _updateValueDisplay() {
    const value = this.value;
    const unit = this.getAttribute("unit") || "";
    this._valueDisplay.textContent = `${value}${unit}`;
  }

  _clearTicks() {
    this._ticks.style.display = "none";
    this._ticks.innerHTML = "";
    this._lastTickCount = null;
    this._lastTickCache = null; // Reset cache to allow re-render when toggled back on
  }

  _renderTicks() {
    if (!this.hasAttribute("show-ticks")) {
      this._ticks.style.display = "none";
      return;
    }

    this._ticks.style.display = "flex";
    
    const min = this.min;
    const max = this.max;
    const step = this.step;
    const unit = this.getAttribute("unit") || "";

    // Calculate number of ticks
    const range = max - min;
    const MAX_TICKS = 11; // Maximum number of ticks to display
    const tickCount = Math.min(Math.floor(range / step) + 1, MAX_TICKS);

    // Check if tick count has changed (avoid unnecessary re-renders)
    const cacheKey = `${tickCount}-${min}-${max}-${unit}`;
    if (this._lastTickCache === cacheKey) return;
    this._lastTickCache = cacheKey;

    // Use DocumentFragment for better performance
    const fragment = document.createDocumentFragment();

    if (tickCount <= MAX_TICKS) {
      // Show actual tick values
      if (tickCount <= 1) {
        // Only a single tick to display; avoid division by zero
        const singleTick = document.createElement("span");
        singleTick.className = "tick";
        singleTick.textContent = `${min}${unit}`;
        fragment.appendChild(singleTick);
      } else {
        const tickStep = range / (tickCount - 1);
        for (let i = 0; i < tickCount; i++) {
          const tickValue = min + (tickStep * i);
          const tick = document.createElement("span");
          tick.className = "tick";
          // Round to 1 decimal place for cleaner display
          tick.textContent = `${Math.round(tickValue * 10) / 10}${unit}`;
          fragment.appendChild(tick);
        }
      }
    } else {
      // Just show min and max
      const minTick = document.createElement("span");
      minTick.className = "tick";
      minTick.textContent = `${min}${unit}`;
      fragment.appendChild(minTick);

      const maxTick = document.createElement("span");
      maxTick.className = "tick";
      maxTick.textContent = `${max}${unit}`;
      fragment.appendChild(maxTick);
    }

    // Replace all children at once
    this._ticks.innerHTML = "";
    this._ticks.appendChild(fragment);
  }

  _onInput(e) {
    // Update value from input
    this.setAttribute("value", this._input.value);
    this._updateProgress();
    this._updateValueDisplay();

    // Emit custom event
    this._emit("ctl-input", {
      name: this.getAttribute("name") || null,
      value: this.value,
      originalEvent: e
    });
  }

  _onChange(e) {
    // Emit custom event
    this._emit("ctl-change", {
      name: this.getAttribute("name") || null,
      value: this.value,
      originalEvent: e
    });
  }

  _emit(name, detail) {
    this.dispatchEvent(
      new CustomEvent(name, {
        bubbles: true,
        composed: true,
        detail
      })
    );
  }
}

customElements.define("ctl-slider", CtlSlider);
